import {useSuspenseQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {CombinedSidebarPayloadResponse} from './types'

/**
 * Shared TanStack Query key for the unified `/_sidebar` combined-payload
 * endpoint. Exported so siblings calling {@link useCombinedSidebarPayload}
 * with the same `(ownerLogin, repoName)` are guaranteed to share a single
 * in-flight request and cache entry.
 */
export function combinedSidebarPayloadQueryKey(ownerLogin: string, repoName: string) {
  return ['code-view-sidebar', 'combined', {ownerLogin, repoName}] as const
}

/**
 * Suspense-fetches the combined lazy sidebar sections from `/${owner}/${repo}/_sidebar`.
 */
export function useCombinedSidebarPayload(ownerLogin: string, repoName: string) {
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister -- Permission-sensitive sidebar slices should refetch instead of restoring persisted data.
  return useSuspenseQuery({
    queryKey: combinedSidebarPayloadQueryKey(ownerLogin, repoName),
    queryFn: async () => {
      const r = await verifiedFetchJSON(`/${ownerLogin}/${repoName}/_sidebar`)
      if (!r.ok) throw new Error(`Combined sidebar fetch failed (${r.status})`)
      return (await r.json()) as CombinedSidebarPayloadResponse
    },
  }).data
}
